import Vue from 'vue';
import store from './vuex/store';
import VueRouter from 'vue-router';
import { sync } from 'vuex-router-sync';
import { loadComponent } from './services';

const languages = ['en', 'de', 'nl', 'pl'];

Vue.use(VueRouter);

/*
 *  Require Authenticated User
 *
 *  First, checks if user is currently authenticated, then checks if the account set-up has been completed.
 *  Optionally, additional checks will be performed which will redirect to the given route or the overview page.
 *  If no callback is provided, the given route will be executed.
 */
function requireAuth(to, from, next, callback)
{
    if ( ! store.state.auth.authenticated)
    {
        next({
            path: Vue.onduro.route('login'),
            query: {redirect: to.fullPath}
        });
    }
    else if ( ! store.state.current.account.setup)
    {
        // user is logged-in, but account set-up is not yet completed
        Vue.modal.show('Account set-up incomplete', 'Your account has not been set-up completely. Please follow the link in your set-up email or contact support!', () =>
        {
            store.dispatch('auth/logout', success =>
            {
                next(Vue.onduro.route('login'));
            });
        });
    }
    else if (typeof callback == 'function')
    {
        // callback will return a LOOSE boolean value (could also be "2")
        if (callback())
        {
            next();
        }
        else
        {
            // redirect to overview page
            next({ path: Vue.onduro.route('overview') });
        }
    }
    else
    {
        next();
    }
}

/**
 * Check for module rights and authorization
 * if 4th argument module is given, check for this instead of 'to.name'
 *
 * @param to
 * @param from
 * @param next
 * @param {String} module
 */
function requireModule(to, from, next, module)
{
    let name = (module)? module : to.name;

    requireAuth(to, from, next, () =>
    {
        return Vue.onduro.hasModule(name);
    });
}

/**
 * Route components
 */
const Login = loadComponent('./pages/login/signin/Page', 'login.signin');
const Password = loadComponent('./pages/login/password/Page', 'login.password');
const Confirmation = loadComponent('./pages/login/confirmation/Page', 'login.confirmation');
const Reset = loadComponent('./pages/login/reset/Page', 'login.reset');
const Signup = loadComponent('./pages/login/signup/Page', 'login.signup');
const Confirm = loadComponent('./pages/login/confirm/Page', 'login.confirm');
const Accept = loadComponent('./pages/login/accept/Page', 'login.accept');
const Welcome = loadComponent('./pages/welcome/Page', 'welcome');
// const Register = loadComponent('./pages/register/Page', 'register');
const Demo = loadComponent('./pages/demo/Page', 'demo');
const TwoFactorSetup = loadComponent('./pages/login/2fa-setup/Page', '2fa.setup');
const TwoFactorVerify = loadComponent('./pages/login/2fa-verify/Page', '2fa.verify');

const Setup = loadComponent('./pages/setup/Page');
const Example = loadComponent('./pages/example', 'example');

const Overview = loadComponent('./pages/overview/Page', 'overview');
const Today = loadComponent('./pages/today/Page', 'today');
const Roster = loadComponent('./pages/roster/Page', 'roster');
const Planning = loadComponent('./pages/planning/Page', 'planning');
const Plannings = loadComponent('./pages/plannings/Page', 'plannings');
const Lists = loadComponent('./pages/lists/Page', 'lists');
const Availability = loadComponent('./pages/availability/Page', 'availability');
const Balances = loadComponent('./pages/balances/overview/Page', 'balances');
const BalancesDetails = loadComponent('./pages/balances/details/Page');
const Reports = loadComponent('./pages/reports/Page', 'reports');
const Profile = loadComponent('./pages/profile/Page', 'profile');
const Notifications = loadComponent('./pages/notifications/Page', 'notifications');
const Hours = loadComponent('./pages/hours/Page', 'hours');
const Contacts = loadComponent('./pages/contacts/Page', 'contacts');
const Team = loadComponent('./pages/team/overview/Page', 'team');
const TeamEdit = loadComponent('./pages/team/edit/Page');
const Resources = loadComponent('./pages/resources/overview/Page', 'resources');
const ResourcesEdit = loadComponent('./pages/resources/edit/Page');
const Log = loadComponent('./pages/log/Page', 'log');
const Modules = loadComponent('./pages/modules/Page', 'modules');
const Dashboard = loadComponent('./pages/dashboard/Page', 'dashboard');
const Clients = loadComponent('./pages/clients/overview/Page', 'clients');
const Company = loadComponent('./pages/company/Page', 'company');
const FreelancerInvoices = loadComponent('./pages/freelancer/Page', 'freelancer');
const Projects = loadComponent('./pages/projects/Page', 'projects');
// const ClientDetails = loadComponent('./pages/clients/details/Page');

const Chats = {
    Overview: loadComponent('./pages/chats/overview/Page', 'chats'),
    Details: loadComponent('./pages/chats/details/Page'),
};

const Settings = {
    Layout: loadComponent('./pages/settings/Page'),
    General: loadComponent('./pages/settings/general/Page', 'settings.general'),
    Company: loadComponent('./pages/settings/company/Page', 'settings.company'),
    Availability: loadComponent('./pages/settings/availability/Page', 'settings.availability'),
    Expenses: loadComponent('./pages/settings/expenses/Page', 'settings.expenses'),
    Locations: loadComponent('./pages/settings/locations/Page', 'settings.locations'),
    Duties: loadComponent('./pages/settings/duties/Page', 'settings.duties'),
    Contracts: loadComponent('./pages/settings/contracts/Page', 'settings.contracts'),
    Shifts: loadComponent('./pages/settings/shifts/Page', 'settings.shifts'),
    Costs: loadComponent('./pages/settings/costs/Page', 'settings.costs'),
    Premiums: loadComponent('./pages/settings/premiums/Page', 'settings.premiums'),
    Rates: loadComponent('./pages/settings/rates/Page', 'settings.rates'),
    Tags: loadComponent('./pages/settings/tags/Page', 'settings.tags'),
    Holidays: loadComponent('./pages/settings/holidays/Page', 'settings.holidays'),
    Advanced: loadComponent('./pages/settings/advanced/Page', 'settings.advanced'),
    Checkin: loadComponent('./pages/settings/checkin/Page', 'settings.checkin'),
};

const Subscription = loadComponent('./pages/subscription/Page', 'subscription');
const Invoice = loadComponent('./pages/invoice/Page', 'invoice');
const Statistics = loadComponent('./pages/statistics/Page', 'statistics');

const Documents = loadComponent('./pages/documents/overview/Page', 'documents')
const DocumentsDetail = loadComponent('./pages/documents/detail/Page', 'documents.detail')

const Checkins = loadComponent('./pages/checkins/Page', 'checkins')

/**
 * Routes
 */
const routes =
[
    /** BACKEND ROUTES **/
    {
        path: '/',
        name: 'home',
        beforeEnter(to, from, next)
        {
            if ( ! store.state.auth.authenticated)
            {
                next(Vue.onduro.route('login'));
            }
            else
            {
                next(Vue.onduro.route('overview'));
            }
        },
        meta: { section: 'backend' },
    },
    {
        path: '/:lang',
        beforeEnter(to, from, next)
        {
            if ( ! store.state.auth.authenticated)
            {
                next(Vue.onduro.route('login'));
            }
            else
            {
                next(Vue.onduro.route('overview'));
            }
        },
        meta: { section: 'backend' },
    },
    // {
    //     path: '/:lang/example',
    //     component: Example,
    //     beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
    //     {
    //         return store.state.app.env == 'local';
    //     }),
    //     meta: { section: 'backend' },
    //     name: 'example',
    // },
    {
        path: '/:lang/overview',
        component: Overview,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'overview',
        alias: ['/:lang/startseite','/:lang/overzicht'],
    },
    {
        path: '/:lang/company',
        component: Company,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'company',
        alias: ['/:lang/unternehmen','/:lang/bedrijf'],
    },
    {
        path: '/:lang/check-ins',
        component: Checkins,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('checkins') && Vue.onduro.isReal() && Vue.onduro.hasRole('supervisor');
        }),
        meta: { section: 'backend' },
        name: 'checkins',
    },
    {
        path: '/:lang/today',
        component: Today,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal();
        }),
        meta: { section: 'backend' },
        name: 'today',
        alias: ['/:lang/heute','/:lang/vandaag'],
    },
    {
        path: '/:lang/roster',
        component: Roster,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal();
        }),
        meta: { section: 'backend' },
        name: 'roster',
        alias: ['/:lang/dienstplan','/:lang/rooster'],
    },
    {
        path: '/:lang/roster/:location/:date',
        component: Roster,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal();
        }),
        meta: { section: 'backend' },
        name: 'roster.item',
        alias: ['/:lang/dienstplan/:location/:date','/:lang/rooster/:location/:date'],
    },
    {
        // this route must be before the next one!
        path: '/:lang/planning/overview/:client?',
        component: Plannings,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'plannings',
        alias: ['/:lang/planning/overzicht/:client?','/:lang/planning/uebersicht/:client?'],
    },
    {
        path: '/:lang/planning/:id?',
        component: Planning,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal() && (Vue.onduro.hasRole('planner') || Vue.onduro.hasRole('observer'));
        }),
        meta: { section: 'backend' },
        name: 'planning',
        alias: ['/:lang/planung/:id?'],
    },
    {
        path: '/:lang/clients',
        component: Clients,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'clients',
        alias: ['/:lang/kunden','/:lang/klanten'],
    },
    // {
    //     path: '/:lang/clients/:id',
    //     component: ClientDetails,
    //     beforeEnter: requireAuth,
    //     meta: { section: 'backend' },
    //     name: 'clients.edit',
    //     alias: ['/:lang/kunden/:id','/:lang/klanten/:id'],
    // },
    {
        path: '/:lang/lists',
        component: Lists,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('lists') && Vue.onduro.hasRole('planner');
        }),
        meta: { section: 'backend' },
        name: 'lists',
        alias: ['/:lang/listen','/:lang/lijsten'],
    },
    {
        path: '/:lang/availability',
        component: Availability,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal() && Vue.onduro.hasModule('availability') && (Vue.onduro.hasRole('planner') || store.state.current.membership.availability);
        }),
        meta: { section: 'backend' },
        name: 'availability',
        alias: ['/:lang/verfuegbarkeit','/:lang/beschikbaarheid'],
    },
    {
        path: '/:lang/contacts',
        component: Contacts,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal() && Vue.onduro.hasModule('contacts') && (Vue.onduro.hasRight('contacts.view') || Vue.onduro.getConfig('contacts.visibility'));
        }),
        meta: { section: 'backend' },
        name: 'contacts',
        alias: ['/:lang/adressbuch','/:lang/adresboek'],
    },
    {
        path: '/:lang/chats',
        component: Chats.Overview,
        beforeEnter: (to, from, next) => requireModule(to, from, next, 'chats'),
        meta: { section: 'backend' },
        name: 'chats',
        alias: ['/:lang/unterhaltungen','/:lang/berichten'],
    },
    {
        path: '/:lang/chats/:channel',
        component: Chats.Details,
        beforeEnter: (to, from, next) => requireModule(to, from, next, 'chats'),
        meta: { section: 'backend' },
        name: 'chats.details',
        alias: ['/:lang/unterhaltungen/:channel','/:lang/berichten/:channel'],
    },
    {
        path: '/:lang/balance',
        component: Balances,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'balances',
        alias: ['/:lang/zeitkonto','/:lang/tijd-voor-tijd'],
    },
    {
        path: '/:lang/balance/:id',
        component: BalancesDetails,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'balances.details',
        alias: ['/:lang/zeitkonto/:id','/:lang/tijd-voor-tijd/:id'],
    },
    {
        path: '/:lang/reports',
        component: Reports,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasRole('accountant');
        }),
        meta: { section: 'backend' },
        name: 'reports',
        alias: ['/:lang/auswertung','/:lang/rapportage'],
    },
    {
        path: '/:lang/profile',
        component: Profile,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'profile',
        alias: ['/:lang/profil','/:lang/profiel'],
    },
    {
        path: '/:lang/notifications',
        component: Notifications,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal();
        }),
        meta: { section: 'backend' },
        name: 'notifications',
        alias: ['/:lang/benachrichtigungen','/:lang/notificaties'],
    },
    {
        path: '/:lang/timesheet',
        component: Hours,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal() && Vue.onduro.hasModule('hours');
        }),
        meta: { section: 'backend' },
        name: 'hours',
        alias: ['/:lang/stundenzettel','/:lang/urenregistratie'],

        children: [
            {
                path: ':page',
                name: 'hours.page',
                meta: { section: 'backend' },
            },
        ]
    },
    {
        path: '/:lang/employees',
        component: Team,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasRight('user.edit');
        }),
        meta: { section: 'backend' },
        name: 'team',
        alias: ['/:lang/mitarbeiter','/:lang/werknemers'],
    },
    {
        path: '/:lang/freelancer-facturen',
        component: FreelancerInvoices,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('freelancers') && (
                Vue.onduro.hasRole(['owner', 'manager', 'accountant', 'freelancer'])
            );
        }),
        meta: { section: 'backend' },
        name: 'freelancer',
        alias: ['/:lang/selbstandig-rechnungen','/:lang/freelancer-invoices'],
    },
    {
        path: '/:lang/projects',
        component: Projects,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('projects') && (
                Vue.onduro.hasRole(['owner', 'manager', 'accountant', 'freelancer'])
            );
        }),
        meta: { section: 'backend' },
        name: 'projects',
        alias: ['/:lang/projekte','/:lang/opdrachten'],
    },
    {
        path: '/:lang/employees/:id',
        component: TeamEdit,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasRight('user.edit');
        }),
        meta: { section: 'backend' },
        name: 'team.edit',
        alias: ['/:lang/mitarbeiter/:id','/:lang/werknemers/:id'],
    },
    {
        path: '/:lang/resources',
        component: Resources,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('resources') && (
                Vue.onduro.hasRight('user.edit') || Vue.onduro.hasRole('planner')
            );
        }),
        meta: { section: 'backend' },
        name: 'resources',
        alias: ['/:lang/ressourcen','/:lang/middelen'],
    },
    {
        path: '/:lang/resources/:id',
        component: ResourcesEdit,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasModule('resources') && (
                Vue.onduro.hasRight('user.edit') || Vue.onduro.hasRole('planner')
            );
        }),
        meta: { section: 'backend' },
        name: 'resources.edit',
        alias: ['/:lang/ressourcen/:id','/:lang/middelen/:id'],
    },
    {
        path: '/:lang/log',
        component: Log,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'log',
        alias: ['/:lang/protokoll'],
    },
    {
        path: '/:lang/modules',
        component: Modules,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal() && Vue.onduro.hasRole('owner', 'manager');
        }),
        meta: { section: 'backend' },
        name: 'modules',
        alias: ['/:lang/module'], // no dutch translation here, because dutch = english (otherwise error!)
    },
    {
        path: '/de/einstellungen',
        meta: { section: 'backend' },
        redirect: '/de/einstellungen/allgemein'
    },
    {
        path: '/nl/instellingen',
        meta: { section: 'backend' },
        redirect: '/nl/instellingen/algemeen'
    },
    {
        path: '/:lang/settings',
        name: 'settings',
        component: Settings.Layout,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasRole('owner', 'manager');
        }),
        alias: ['/:lang/einstellungen','/:lang/instellingen'],
        meta: { section: 'backend' },
        redirect: { name: 'settings.general' },

        children: [
            {
                path: 'general',
                name: 'settings.general',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/allgemein','/:lang/instellingen/algemeen'],
                meta: { section: 'backend' },
                component: Settings.General,
            },
            {
                path: 'availability',
                name: 'settings.availability',
                beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
                {
                    return Vue.onduro.isReal();
                }),
                alias: ['/:lang/einstellungen/verfuegbarkeit','/:lang/instellingen/beschikbaarheid'],
                meta: { section: 'backend' },
                component: Settings.Availability,
            },
            {
                path: 'expenses',
                name: 'settings.expenses',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/unkosten','/:lang/instellingen/onkosten'],
                meta: { section: 'backend' },
                component: Settings.Expenses,
            },
            {
                path: 'locations',
                name: 'settings.locations',
                beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
                {
                    return Vue.onduro.isReal();
                }),
                alias: ['/:lang/einstellungen/standorte','/:lang/instellingen/locaties'],
                meta: { section: 'backend' },
                component: Settings.Locations,
            },
            {
                path: 'functions',
                name: 'settings.duties',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/funktionen','/:lang/instellingen/functies'],
                meta: { section: 'backend' },
                component: Settings.Duties,
            },
            {
                path: 'contracts',
                name: 'settings.contracts',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/vertraege','/:lang/instellingen/contracten'],
                meta: { section: 'backend' },
                component: Settings.Contracts,
            },
            {
                path: 'shifts',
                name: 'settings.shifts',
                beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
                {
                    return Vue.onduro.isReal();
                }),
                alias: ['/:lang/einstellungen/schichten','/:lang/instellingen/shifts'],
                meta: { section: 'backend' },
                component: Settings.Shifts,
            },
            {
                path: 'costs',
                name: 'settings.costs',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/kostenstellen','/:lang/instellingen/kostenplaatsen'],
                meta: { section: 'backend' },
                component: Settings.Costs,
            },
            {
                path: 'premiums',
                name: 'settings.premiums',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/zuschlaege','/:lang/instellingen/toeslagen'],
                meta: { section: 'backend' },
                component: Settings.Premiums,
            },
            {
                path: 'rates',
                name: 'settings.rates',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/stundenloehne','/:lang/instellingen/uurlonen'],
                meta: { section: 'backend' },
                component: Settings.Rates,
            },
            {
                path: 'tags',
                name: 'settings.tags',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/tags','/:lang/instellingen/tags'],
                meta: { section: 'backend' },
                component: Settings.Tags,
            },
            {
                path: 'holidays',
                name: 'settings.holidays',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/feiertage','/:lang/instellingen/feestdagen'],
                meta: { section: 'backend' },
                component: Settings.Holidays,
            },
            {
                path: 'advanced',
                name: 'settings.advanced',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/erweitert','/:lang/instellingen/geavanceerd'],
                meta: { section: 'backend' },
                component: Settings.Advanced,
            },
            {
                path: 'check-in',
                name: 'settings.checkin',
                beforeEnter: requireAuth,
                alias: ['/:lang/einstellungen/check-in','/:lang/instellingen/check-in'],
                meta: { section: 'backend' },
                component: Settings.Checkin,
            },
        ]
    },
    {
        path: '/:lang/subscription',
        component: Subscription,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.hasRole('owner');
        }),
        meta: { section: 'backend' },
        name: 'subscription',
        alias: ['/:lang/abonnement'],
    },
    {
        path: '/:lang/subscription/confirm',
        component: Subscription,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'subscription.confirm',
        alias: ['/:lang/abonnement/bestaetigen','/:lang/abonnement/bevestigen'],
    },
    {
        path: '/:lang/invoice/:id',
        component: Invoice,
        beforeEnter: requireAuth,
        meta: { section: 'invoice' },
        name: 'invoice',
        alias: ['/:lang/rechnung/:id','/:lang/factuur/:id'],
    },
    {
        path: '/:lang/statistics',
        component: Statistics,
        beforeEnter: (to, from, next) => requireAuth(to, from, next, () =>
        {
            return Vue.onduro.isReal();
        }),
        meta: { section: 'backend' },
        name: 'statistics',
        alias: ['/:lang/statistiken','/:lang/statistieken'],
    },
    {
        path: '/:lang/documents',
        component: Documents,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'documents',
        alias: ['/:lang/dokumente','/:lang/documenten'],
    },
    {
        path: '/:lang/documents/:type',
        component: DocumentsDetail,
        beforeEnter: requireAuth,
        meta: { section: 'backend' },
        name: 'documents.detail',
        alias: ['/:lang/dokumente/:type','/:lang/documenten/:type'],
    },
    {
        path: '/:lang/i/:id/:hash',
        beforeEnter(to, from, next) {
            location.href = 'https://data.onduro.com/invoices/' + to.params.id + '/' + to.params.hash;
            next('/');
        }
    },

    /** FRONTEND ROUTES **/

    {
        path: '/:lang/demo',
        component: Demo,
        name: 'demo',
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            if (store.state.auth.authenticated) {
                next(to.query.redirect || Vue.onduro.route('overview'));
            } else {
                next();
            }
        },
    },
    // {
    //     path: '/:lang/register',
    //     component: Register,
    //     name: 'register',
    //     alias: ['/:lang/registrieren','/:lang/registreren'],
    //     meta: { section: 'frontend' },
    //     beforeEnter(to, from, next) {
    //         if (store.state.auth.authenticated) {
    //             next(to.query.redirect || Vue.onduro.route('overview'));
    //         } else {
    //             next();
    //         }
    //     },
    // },
    {
        path: '/:lang/login/signin',
        component: Login,
        name: 'login.signin',
        alias: ['/:lang/login/anmeldung','/:lang/login/aanmelden'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            if (store.state.auth.authenticated) {
                next(to.query.redirect || Vue.onduro.route('overview'));
            } else {
                next();
            }
        },
    },
    {
        path: '/:lang/login/forgot-password',
        component: Password,
        name: 'login.password',
        alias: ['/:lang/login/passwort-vergessen','/:lang/login/wachtwoord-vergeten'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            if (store.state.auth.authenticated) {
                next(to.query.redirect || Vue.onduro.route('overview'));
            } else {
                next();
            }
        },
    },
    {
        path: '/:lang/login/request-confirmation',
        component: Confirmation,
        name: 'login.confirmation',
        alias: ['/:lang/login/bestaetigungscode-anfordern','/:lang/login/bevestigingscode-aanvragen'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            if (store.state.auth.authenticated) {
                next(to.query.redirect || Vue.onduro.route('overview'));
            } else {
                next();
            }
        },
    },
    {
        path: '/:lang/login/reset-password/:code',
        component: Reset,
        name: 'login.reset',
        alias: ['/:lang/login/passwort-zuruecksetzen/:code','/:lang/login/wachtwoord-opnieuw-instellen/:code'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            // make sure user is logged out on this page
            store.dispatch('auth/logout', success =>
            {
                next();
            });
        },
    },
    {
        path: '/:lang/login/signup/:code',
        component: Signup,
        name: 'login.signup',
        alias: ['/:lang/login/registrieren/:code','/:lang/login/registreren/:code'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            // make sure user is logged out on this page
            store.dispatch('auth/logout', success =>
            {
                next();
            });
        },
    },
    {
        path: '/:lang/login/confirm-email/:code/:account?',
        component: Confirm,
        name: 'login.confirm',
        alias: ['/:lang/login/email-bestaetigen/:code/:account?','/:lang/login/email-bevestigen/:code/:account?'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            // make sure user is logged out on this page
            store.dispatch('auth/logout', success =>
            {
                next();
            });
        },
    },
    {
        path: '/:lang/login/accept-invitation/:code',
        component: Accept,
        name: 'login.accept',
        alias: ['/:lang/login/einladung-akzeptieren/:code','/:lang/login/uitnodiging-accepteren/:code'],
        meta: { section: 'frontend' },
        beforeEnter(to, from, next) {
            // make sure user is logged out on this page
            store.dispatch('auth/logout', success =>
            {
                next();
            });
        },
    },
    {
        path: '/:lang/login/2fa/verify',
        component: TwoFactorVerify,
        name: '2fa.verify',
        alias: ['/:lang/login/2fa/verifizieren','/:lang/login/2fa/verifieren'],
        meta: { section: 'frontend' },
    },
    {
        path: '/:lang/login/2fa/setup',
        component: TwoFactorSetup,
        name: '2fa.setup',
        alias: ['/:lang/login/2fa/einrichten','/:lang/login/2fa/inrichten'],
        meta: { section: 'frontend' },
    },

    /** EMPTY TEMPLATE **/

    {
        path: '/:lang/welcome',
        component: Welcome,
        name: 'welcome',
        alias: ['/:lang/willkommen','/:lang/welkom'],
        meta: { section: 'empty' },
        beforeEnter(to, from, next) {
            if (store.state.auth.authenticated) {
                next(to.query.redirect || Vue.onduro.route('overview'));
            } else {
                next();
            }
        },
    },
    {
        path: '/:lang/dashboard/:id/:hash',
        component: Dashboard,
        name: 'dashboard',
        meta: { section: 'empty' },
        // beforeEnter(to, from, next) {
        //     next();
        // },
    },

    /** SETUP ROUTES **/

    {
        path: '/:lang/setup/:id/:token',
        name: 'setup',
        component: Setup,
        meta: { section: 'wizard' },
        beforeEnter: (to, from, next) =>
        {
            store.dispatch('auth/logout', success =>
            {
                next();
            });
        },
    },

    {
        path: '/:lang/logout',
        beforeEnter(to, from, next) {

            if (store.state.layout.viewport == 'ml' || store.state.layout.viewport == 'mp')
            {
                Vue.confirm.show(Vue.t('page.logout.confirm.headline'), Vue.t('page.logout.confirm.body'),
                {
                    confirm()
                    {
                        store.dispatch('auth/logout', success =>
                        {
                            next(Vue.onduro.route('login'));
                        });
                    }
                },
                {
                    confirm: Vue.t('common.button.confirm'),
                    decline: Vue.t('common.button.decline')
                });

                return false;
            }

            store.dispatch('auth/logout', success =>
            {
                next(Vue.onduro.route('login'));
            });
        },
    },
];

let router = new VueRouter({
    routes: routes,
    mode: 'history',
    // scrollBehavior (to, from, savedPosition)
    // {
    //     console.info(savedPosition);
    //     if (savedPosition) {
    //         return savedPosition
    //     } else {
    //         return { x: 0, y: 0 }
    //     }
    // }
});

// sync router and store
sync(store, router);

// configure default route handling
router.beforeEach((to, from, next) =>
{
    /*
     * Route translation for URLs from email
     *
     * Usage: {{event.app_url}}/translate/{{customer.language}}?route=login/confirm&suffix={{event.code}}/{{event.hash}}
     * Example: .../translate/de?route=login/reset&suffix=52afeb7ad50a66b6eaa3d1827c08dce1
     */
    if (to.path.indexOf('/translate') === 0) {

        let lang = to.path.split('/')[2];

        if (['de', 'nl', 'en', 'pl'].indexOf(lang) !== -1)
        {
            let route = Vue.onduro.route(to.query.route, null, lang);

            if (to.query.suffix) route += '/' + to.query.suffix;

            store.dispatch('meta/setLanguage', lang);

            next(route);
        }
        else
        {
            next('/');
        }
    }

    // handle url redirects
    else if ( ! store.state.meta.lang || to.params.lang != store.state.meta.lang) {

        // no locale set in path
        if (to.path == '/') {

            // try to get locale from localStorage
            let locale = localStorage.getItem('locale');

            if ( ! locale)
            {
                let browserLang = navigator.language || navigator.userLanguage;
                locale = (languages.indexOf(browserLang) != -1) ? browserLang : 'en';
            }

            // no language set in path
            store.dispatch('meta/setLanguage', locale);

        } else if ( ! to.matched.length) {

            // try to get language from path
            let segments = to.path.split('/');

            if (languages.indexOf(segments[1]) !== -1) {
                // language exists, redirect to home
                next(segments[1]);
            } else {
                // language does not exist, redirect to root
                next('/');
            }

        } else if (languages.indexOf(to.params.lang) === -1) {

            // invalid language segment
            next('/');

        } else if (Vue.onduro.translatedRoutePath(to, to.params.lang) != to.path) {

            // set language
            store.dispatch('meta/setLanguage', to.params.lang);

            // redirect to the correctly translated path
            next({
                path: Vue.onduro.translatedRoutePath(to, to.params.lang),
                query: to.query
            });

        } else {

            // set language from path
            store.dispatch('meta/setLanguage', to.params.lang);
        }
    }

    // wait for the "current" store to be loaded...
    Vue.onduro.isReady(() =>
    {
        if (window.Intercom) Intercom('update');
        next();
    });
});

router.afterEach((to, from, next) => {
    // do stuff after route changed (!)
    if (store.state.layout.mobile) store.dispatch('layout/hideNavigation');
});

export default router;



// WEBPACK FOOTER //
// ./src/js/router.js